import { Component, Input, ChangeDetectionStrategy } from '@angular/core';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatChipsModule } from '@angular/material/chips';
import { MatTooltipModule } from '@angular/material/tooltip';

export interface ArticleDisplayItem {
  identifier: string;
  score: number;           // 0.0 – 1.0
  category: string;
  subCategory: string;
  question: string;
  content: string;
  action?: string | null;
  requestKey?: string | null;
}

export interface ArticleDisplaySection {
  label: string;
  style: 'blue' | 'gray' | 'red';
  articles: ArticleDisplayItem[];
  proposedAction?: string | null;
}

@Component({
  selector: 'app-article-list',
  standalone: true,
  imports: [MatExpansionModule, MatChipsModule, MatTooltipModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: [`
    /* Compact scrollbar */
    .compact-scroll {
      scrollbar-width: thin;
      scrollbar-color: #cbd5e1 transparent;
    }
    .compact-scroll::-webkit-scrollbar        { width: 3px; }
    .compact-scroll::-webkit-scrollbar-track  { background: transparent; }
    .compact-scroll::-webkit-scrollbar-thumb  { background: #cbd5e1; border-radius: 2px; }
    .compact-scroll::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
    .dark .compact-scroll { scrollbar-color: #4b5563 transparent; }
    .dark .compact-scroll::-webkit-scrollbar-thumb       { background: #4b5563; }
    .dark .compact-scroll::-webkit-scrollbar-thumb:hover { background: #6b7280; }

    /* Small chip inside panel header */
    .header-chip.mat-mdc-chip { height: 18px; min-height: unset; padding: 0 6px; }
    .header-chip .mdc-evolution-chip__text-label { font-size: 0.6rem; }

    /* Responsive action chip */
    .action-chip-responsive { display: none; }
    @media (min-width: 650px) { .action-chip-responsive { display: inline-flex; } }

    /* Allow question span to use all available width */
    ::ng-deep .mat-expansion-panel-header-title {
      min-width: 0;
      overflow: hidden;
    }
  `],
  template: `
    @for (section of sections; track section.label) {
      <div class="mb-4">

        <!-- Section header -->
        <div [class]="headerClass(section.style)" class="p-2 rounded-t text-xs flex items-center gap-2">
          <span class="font-medium flex-1">{{ section.label }}</span>
          @if (section.proposedAction) {
            <mat-chip class="text-xs">{{ section.proposedAction }}</mat-chip>
          }
          <span class="font-semibold">{{ section.articles.length }}</span>
        </div>

        <!-- Articles accordion -->
        @if (section.articles.length > 0) {
          <div class="rounded-b-lg overflow-hidden">
          <mat-accordion>
            @for (article of section.articles; track article.identifier) {
              <mat-expansion-panel>
                <mat-expansion-panel-header>
                  <mat-panel-title class="text-black dark:text-white flex items-center gap-2 min-w-0">
                    <span [class]="scoreClass(section.style)" class="text-xs font-bold flex-shrink-0">
                      {{ (article.score * 100).toFixed(1) }}%
                    </span>
                    <span class="font-medium flex-shrink-0">{{ article.identifier || 'Sin ID' }}</span>
                    <span
                      class="text-light-text-tertiary dark:text-dark-text-tertiary line-clamp-2 text-xs min-w-0 flex-1"
                      [matTooltip]="article.question"
                      matTooltipShowDelay="400">{{ article.question }}</span>
                    @if (article.action) {
                      <mat-chip
                        [color]="section.style === 'red' ? 'warn' : 'accent'"
                        class="header-chip flex-shrink-0 action-chip-responsive">{{ article.action }}</mat-chip>
                    }
                  </mat-panel-title>
                </mat-expansion-panel-header>

                <!-- Expanded content -->
                <div [class]="contentBgClass(section.style)" class="p-3 space-y-3 text-black dark:text-white text-xs">
                  <div class="flex gap-4">
                    <span><strong>Cat:</strong> {{ article.category || 'N/A' }}</span>
                    <span><strong>Sub:</strong> {{ article.subCategory || 'N/A' }}</span>
                  </div>
                  <div>
                    <strong class="block mb-1">Pregunta:</strong>
                    <div [class]="questionBgClass(section.style)" class="p-2 rounded">
                      {{ article.question || 'N/A' }}
                    </div>
                  </div>
                  <div>
                    <strong class="block mb-1">Respuesta:</strong>
                    <div class="bg-light-bg-secondary dark:bg-dark-bg-secondary p-2 rounded compact-scroll max-h-32 overflow-y-auto whitespace-pre-line">
                      {{ article.content || 'N/A' }}
                    </div>
                  </div>
                  @if (article.action || article.requestKey) {
                    <div class="grid md:grid-cols-3 gap-2">
                      @if (article.action) {
                        <div>
                          <strong>Acción:</strong>
                          <mat-chip [color]="section.style === 'red' ? 'warn' : 'accent'" class="text-xs">{{ article.action }}</mat-chip>
                        </div>
                      }
                      @if (article.requestKey) {
                        <div class="md:col-span-2">
                          <strong>Key:</strong>
                          <span class="font-mono text-xs bg-light-bg-tertiary dark:bg-dark-bg-tertiary px-1 rounded">{{ article.requestKey }}</span>
                        </div>
                      }
                    </div>
                  }
                </div>
              </mat-expansion-panel>
            }
          </mat-accordion>
          </div>
        } @else {
          <div class="text-xs text-light-text-tertiary dark:text-dark-text-tertiary p-3 text-center border border-t-0 rounded-b">
            Sin artículos
          </div>
        }

      </div>
    }
  `,
})
export class ArticleListComponent {
  @Input() sections: ArticleDisplaySection[] = [];

  headerClass(style: ArticleDisplaySection['style']): string {
    return {
      blue: 'bg-blue-50 dark:bg-blue-900/20 text-black dark:text-white',
      gray: 'bg-light-bg-secondary dark:bg-dark-bg-secondary text-black dark:text-white',
      red:  'bg-red-50 dark:bg-red-900/20 text-black dark:text-white border-l-4 border-red-500',
    }[style];
  }

  scoreClass(style: ArticleDisplaySection['style']): string {
    return style === 'red' ? 'text-red-500' : 'text-blue-500';
  }

  contentBgClass(style: ArticleDisplaySection['style']): string {
    return style === 'red' ? 'bg-red-50/50 dark:bg-red-900/10' : '';
  }

  questionBgClass(style: ArticleDisplaySection['style']): string {
    return style === 'red'
      ? 'bg-red-50 dark:bg-red-900/20'
      : 'bg-blue-50 dark:bg-blue-900/20';
  }
}
